import React, {Component} from 'react';

import Select from 'components/common/select/Select.component';
import { FormField } from 'components/common/FormField.component';
import _ from "lodash";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
// How close to the end of the list counts as "reached the bottom", in pixels.
const SCROLL_END_THRESHOLD = 24;

const ANY_VALUE = -99;
const UNASSIGNED_VALUE = -1;

const COMMON_PARAMS = {
    prefix: '',
    page: 1,
    rows: PAGE_SIZE,
    usedInIndexOnly: true,
    showInactive: true
};

const toOption = (item) => ({
    label: (item.number > 0 || typeof item.number === 'string' ? (item.number + " - ") : "") + item.name,
    value: item.number,
    color: (item.active) ? 'black' : 'lightgrey'
});


export class AdvancedSearch extends Component {
    constructor(props) {
        super(props);
        this.state = {
            options: [],
            isLoading: false,
        };

        // Paging bookkeeping is kept off state: the scroll handler and the lookup callbacks need
        // the current values, not the ones captured when they were created.
        this.page = 1;
        this.prefix = '';
        this.hasMore = false;
        this.inFlight = false;
        // Monotonic token so a superseded lookup - a stale search, or a page that arrives after
        // the user typed again - cannot overwrite the options of the request that replaced it.
        this.requestId = 0;
        this.request = null;

        this.loadPage = this.loadPage.bind(this);
        this.buildSpecialOptions = this.buildSpecialOptions.bind(this);
        this.openMenu = this.openMenu.bind(this);
        this.inputChanged = this.inputChanged.bind(this);
        this.menuListScrolled = this.menuListScrolled.bind(this);
        this.cancelCalls = this.cancelCalls.bind(this);
        this.focus = this.focus.bind(this);
        this.searchWithDelay = _.debounce((value) => {
            this.loadPage(value, 1);
        }, SEARCH_DEBOUNCE_MS);
        this.inputElement = React.createRef();
    }


    focus() {
        if (this.inputElement && typeof this.inputElement.focus === "function") {
            this.inputElement.focus();
        }
    }

    componentWillUnmount() {
        this.searchWithDelay.cancel();
        this.cancelCalls();
    }

    cancelCalls() {
        // Bumping the token is what actually discards a response; cancelling saves the round trip.
        this.requestId++;
        this.inFlight = false;
        if (this.request) {
            if (typeof this.request.cancel === "function") {
                this.request.cancel();
            }
            this.request = null;
        }
    }

    /**
     * "Any" and "Unassigned" are client-side entries, so they are matched against the search term
     * here - the backend knows nothing about them. They only belong on the first page.
     * @param {string} prefix
     */
    buildSpecialOptions(prefix) {
        const needle = (prefix || '').trim().toLowerCase();
        const specials = [];
        if (this.props.hasUnassigned)
            specials.push({number: UNASSIGNED_VALUE, name: this.props.translate("generic.unassigned"), active: true});
        if (this.props.hasAny)
            specials.push({number: ANY_VALUE, name: this.props.translate("generic.genericAny"), active: true});

        return specials
            .filter(item => !needle || item.name.toLowerCase().includes(needle))
            .map(toOption);
    }

    /**
     * @param {string} prefix search term, applied by the backend
     * @param {number} page 1 replaces the options, anything higher appends to them
     */
    loadPage(prefix, page) {
        const func = this.props.type;
        const dimension = this.props.dimension;
        if (typeof dimension === "undefined" || typeof func !== "function")
            return;

        const requestId = ++this.requestId;
        this.inFlight = true;
        this.prefix = prefix;
        if (page === 1) {
            this.setState({isLoading: true});
        }

        // Returns false when a newer request has already taken over.
        const isCurrent = () => {
            if (requestId !== this.requestId)
                return false;
            this.inFlight = false;
            this.request = null;
            this.setState({isLoading: false});
            return true;
        };

        const data = Object.assign({}, COMMON_PARAMS, {
            prefix: prefix,
            page: page,
            usedInIndexOnly: this.props.usedInIndexOnly !== false
        });

        this.request = func(data, dimension).then(response => {
            if (!isCurrent())
                return;
            const rows = (response && response.rows) || [];
            this.page = page;
            // A short page means the end of the list. Deriving it from the row count keeps paging
            // working regardless of whether the response carries a usable total.
            this.hasMore = rows.length === PAGE_SIZE;
            const fetched = rows.map(toOption);
            this.setState(previous => ({
                options: page === 1
                    ? this.buildSpecialOptions(prefix).concat(fetched)
                    : previous.options.concat(fetched)
            }));
        }, () => {
            if (!isCurrent())
                return;
            this.hasMore = false;
            if (page === 1) {
                this.setState({options: this.buildSpecialOptions(prefix)});
            }
        });
    }

    openMenu() {
        // react-select clears the input when the menu closes, so every open starts unfiltered.
        this.searchWithDelay.cancel();
        this.hasMore = false;
        this.setState({options: []});
        this.loadPage('', 1);
    }

    /**
     * Search has to go to the backend: without it the input would only filter the single page of
     * options already loaded, hiding every cost unit past the first PAGE_SIZE.
     */
    inputChanged(value, actionMeta) {
        if (!actionMeta || actionMeta.action !== "input-change")
            return;
        this.searchWithDelay(value);
    }

    menuListScrolled(event) {
        if (this.inFlight || !this.hasMore)
            return;
        const {scrollTop, scrollHeight, clientHeight} = event.currentTarget;
        if (scrollHeight - scrollTop - clientHeight <= SCROLL_END_THRESHOLD) {
            this.loadPage(this.prefix, this.page + 1);
        }
    }


    render() {
         return (
             <div aria-labelledby={"search" + this.props.name + "txt"} role="listitem">
                        <FormField
                            id={"search" + this.props.name}
                            label={this.props.name || ""}
                        >
                            <Select
                                isClearable={true}
                                ref={(element) => {
                                    this.inputElement = element;
                                }}
                                aria-labelledby={"search" + this.props.name + "txt"}
                                isDisabled={this.props.disabled}
                                isSearchable={true}
                                isLoading={this.state.isLoading}
                                onBlurResetsInput={false}
                                onCloseResetsInput={false}
                                value={this.props.selectedValue}
                                placeholder={this.props.placeholder}
                                showNoOptions={false}
                                menuPlacement={this.props.menuPlacement ? this.props.menuPlacement : "bottom"}
                                menuPosition={this.props.menuPosition ? this.props.menuPosition : "absolute"}
                                menuPortalTarget={this.props.menuPortalTarget ? this.props.menuPortalTarget :document.body}
                                tabIndex={this.props.tabIndex}
                                autoFocus={this.props.autoFocus}
                                options={this.state.options}
                                tabSelectsValue={this.props.tabSelectsValue ? this.props.tabSelectsValue : false}
                                /* The backend already applied the search term; filtering again on
                                   the client would only be able to hide rows it returned. */
                                filterOption={null}
                                onMenuOpen={this.openMenu}
                                onInputChange={this.inputChanged}
                                onChange={this.props.onChange.bind(this)}
                                onMenuListScroll={this.menuListScrolled}
                                closeMenuOnSelect={true}
                                translate={this.props.translate}
                            />
                        </FormField>
                    </div>

        );
    }
    }
